/*
  See https://ajv.js.org/keywords.html
*/
import Ajv, {
  FuncKeywordDefinition,
  SchemaValidateFunction,
  ValidationError,
} from 'ajv';
import emailRegex from 'email-regex';
import * as fs from 'fs';
import * as path from 'path';
import parseAuthor from 'parse-author';
import * as semver from 'semver';
import validatePackageJSON from './validatePackageJSON';
import semverValidRange from 'semver/ranges/valid';

export default function addCustomKeywords(
  ajv: Ajv,
  context: {
    projectRoot: string;
  },
) {
  const addKeyword = (def: FuncKeywordDefinition) => {
    const validate: SchemaValidateFunction = (schema, data) => {
      try {
        return (def.validate as SchemaValidateFunction)(schema, data);
      } catch (e) {
        if (e instanceof ValidationError) {
          validate.errors = e.errors;
          return false;
        }

        throw e;
      }
    };

    ajv.addKeyword({ ...def, validate });
  };

  addKeyword({
    keyword: 'validSemver',
    type: 'string',
    validate: (_schema: boolean, data: string) => {
      if (!semverValidRange(data)) {
        throw new ValidationError([
          {
            keyword: 'validSemver',
            message: `${data} must be a valid semantic version or version range`,
          },
        ]);
      }

      return true;
    },
  });

  addKeyword({
    keyword: 'validSemverOrLatest',
    type: 'string',
    validate: (_schema: boolean, data: string) => {
      if (data === 'latest') {
        return true;
      }

      if (!semverValidRange(data)) {
        throw new ValidationError([
          {
            keyword: 'validSemverOrLatest',
            message: `${data} must be "latest" or a valid semantic version or version range`,
          },
        ]);
      }

      return true;
    },
  });

  // validate that dependencies object doesn't contain packageNames in the blacklist
  addKeyword({
    keyword: 'excludedDependencies',
    validate: (
      schema: Record<string, unknown>,
      data: Record<string, unknown>,
    ) => {
      const matchingPackages = (schema.blacklist as string[]).filter(
        (packageName: string) => data[packageName],
      );

      if (matchingPackages.length) {
        throw new ValidationError([
          {
            keyword: 'Dependency',
            message: `${matchingPackages.join(', ')} should not be in ${
              schema.dependencyKey
            }`,
            instancePath: `/${schema.dependencyKey}`,
          },
        ]);
      }

      return true;
    },
  });

  // TODO: Remove if isn't used
  addKeyword({
    keyword: 'email',
    type: 'string',
    validate: (_schema: boolean, data: string) => {
      if (!emailRegex({ exact: true }).test(data)) {
        throw new ValidationError([
          {
            keyword: 'email',
            message: 'invalid',
          },
        ]);
      }
      return true;
    },
  });

  addKeyword({
    keyword: 'file',
    type: 'string',
    validate: (schema: Record<string, any>, data: string) => {
      const mustBeDirectory =
        schema.mustBeDirectory || schema.mustBeElectronApp;

      // Value is empty
      if (!data.trim().length) {
        if (schema.isOptional) {
          return true;
        }

        throw new ValidationError([
          {
            keyword: schema.mustBeDirectory ? 'Directory' : 'File',
            message: 'is empty',
          },
        ]);
      }

      const filePath = path.isAbsolute(data)
        ? data
        : path.join(schema.from, data);

      // Isn't in project
      if (path.relative(schema.from, filePath).startsWith('..')) {
        throw new ValidationError([
          {
            keyword: mustBeDirectory ? 'Directory' : 'File',
            message: `is not in project (${filePath})`,
          },
        ]);
      }

      // Extension isn't allowed
      if (
        schema.extensions &&
        !schema.extensions.includes(path.extname(filePath).substr(1))
      ) {
        throw new ValidationError([
          {
            keyword: 'File extension',
            message: `invalid. ${path.extname(data)} Must be${
              schema.extensions > 1 ? 'one of the following:' : ''
            } "${schema.extensions.join('", "')}"`,
          },
        ]);
      }

      // Doesn't exist
      if (!fs.existsSync(filePath)) {
        throw new ValidationError([
          {
            keyword: mustBeDirectory ? 'Directory' : 'File',
            message: `does not exist (${filePath})`,
          },
        ]);
      }

      const stats = fs.statSync(filePath);
      const extension = path.extname(filePath).substr(1).toLowerCase();
      const allowDirectoryExtensions = Array.isArray(
        schema.allowDirectoryExtensions,
      )
        ? schema.allowDirectoryExtensions
        : [];
      const mustBeDirectoryExtensions = Array.isArray(
        schema.mustBeDirectoryExtensions,
      )
        ? schema.mustBeDirectoryExtensions
        : [];

      if (mustBeDirectory && stats.isFile()) {
        throw new ValidationError([
          {
            keyword: 'Directory',
            message: `must be a directory, not a file (${filePath})`,
          },
        ]);
      } else if (
        mustBeDirectoryExtensions.includes(extension) &&
        stats.isFile()
      ) {
        throw new ValidationError([
          {
            keyword: 'Directory',
            message: `must be a directory, not a file (${filePath})`,
          },
        ]);
      } else if (
        schema.mustBeFile &&
        stats.isDirectory() &&
        !allowDirectoryExtensions.includes(extension)
      ) {
        throw new ValidationError([
          {
            keyword: 'File',
            message: `must be a file, not a directory (${filePath})`,
          },
        ]);
      }

      if (schema.mustBeElectronApp) {
        const appRoot = path.resolve(filePath);
        const pkgPath = path.join(appRoot, 'package.json');

        if (!fs.existsSync(pkgPath)) {
          throw new ValidationError([
            {
              keyword: 'App',
              message: `invalid. There is no package.json at ${pkgPath}`,
            },
          ]);
        }

        let pkg;
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          pkg = require(pkgPath);
        } catch {
          throw new ValidationError([
            {
              keyword: 'App',
              message: `invalid. Invalid JSON in package.json (${pkgPath})`,
            },
          ]);
        }

        context.projectRoot = appRoot;
        // This throws and bubbles all the way up to the user
        validatePackageJSON(pkg, pkgPath, context);

        // Validate that either an index.js or valid "main" property exists
        const mainFilePath = pkg.main;
        if (mainFilePath) {
          const resolvedMainFilePath = path.join(appRoot, mainFilePath);
          if (!fs.existsSync(resolvedMainFilePath)) {
            throw new ValidationError([
              {
                keyword: 'App',
                message: `invalid. The "main" file specified in your package.json (${mainFilePath}) does not exist (${resolvedMainFilePath})`,
              },
            ]);
          }
        } else if (!fs.existsSync(path.join(appRoot, 'index.js'))) {
          throw new ValidationError([
            {
              keyword: 'App',
              message:
                'invalid. There is neither a "main" property in your package.json nor an index.js at the root of your app',
            },
          ]);
        }
      }

      return true;
    },
    errors: true,
  });

  addKeyword({
    keyword: 'npmAuthor',
    validate: (_schema: boolean, data: any) => {
      const dataType = typeof data;
      if (dataType === 'undefined') {
        return true;
      } else if (dataType === 'string') {
        const parsedAuthor = parseAuthor(data);
        if (!parsedAuthor || !parsedAuthor.email) {
          throw new ValidationError([
            {
              keyword: 'author',
              message:
                'invalid. If you\'re using a string, it must look something like "Barney Rubble <b@rubble.com>", see https://docs.npmjs.com/files/package.json#people-fields-author-contributors',
            },
          ]);
        }
      } else if (dataType === 'object') {
        if (typeof data.name === 'undefined') {
          throw new ValidationError([
            {
              keyword: 'author',
              message: `invalid. "name" property is missing.`,
            },
          ]);
        } else {
          if (typeof data.name !== 'string') {
            throw new ValidationError([
              {
                keyword: 'author',
                message: `.name invalid. It must be a string`,
              },
            ]);
          }

          if (typeof data.email === 'undefined') {
            throw new ValidationError([
              {
                keyword: 'author',
                message: `invalid. "email" property is missing.`,
              },
            ]);
          } else if (typeof data.email === 'string') {
            if (!emailRegex({ exact: true }).test(data.email)) {
              throw new ValidationError([
                {
                  keyword: 'author',
                  message: `.email invalid. It does not look like an email.`,
                },
              ]);
            }
          } else {
            throw new ValidationError([
              {
                keyword: 'author',
                message: `.email invalid. It must be a string.`,
              },
            ]);
          }
        }
      } else {
        // Neither object nor string
        throw new ValidationError([
          {
            keyword: 'author',
            message:
              'invalid. It must either be an object or string. See https://docs.npmjs.com/files/package.json#people-fields-author-contributors',
          },
        ]);
      }

      return true;
    },
    errors: true,
  });

  addKeyword({
    keyword: 'semanticVersion',
    type: 'string',
    validate: (schema: Record<string, any>, data: string) => {
      const keyword =
        (schema.packageName ? `${schema.packageName} ` : '') + 'version';

      if (!(semver.valid(data) || semver.validRange(data))) {
        throw new ValidationError([
          {
            keyword,
            message:
              'invalid. It must be a semantic version (see https://semver.org/)',
          },
        ]);
      }

      if (
        schema.mustBeExact &&
        (data.startsWith('~') || data.startsWith('^'))
      ) {
        throw new ValidationError([
          {
            keyword,
            message:
              'invalid. It must be an exact version. Good: 9.0.0. Bad: ^9.0.0',
          },
        ]);
      }
      return true;
    },
  });

  addKeyword({
    keyword: 'macIconComposerAppBuilderLibVersion',
    type: 'object',
    validate: (_schema: boolean, data: Record<string, any>) => {
      const macIcon = data?.mac?.icon;
      if (
        typeof macIcon !== 'string' ||
        path.extname(macIcon).toLowerCase() !== '.icon'
      ) {
        return true;
      }

      const minSupportedVersion = '26.8.1';
      const configuredVersion = data?.appBuilderLibVersion;
      if (configuredVersion === 'latest') {
        return true;
      }

      const resolvedVersion = configuredVersion
        ? semver.minVersion(configuredVersion)
        : null;

      if (
        !resolvedVersion ||
        !semver.gte(resolvedVersion, minSupportedVersion)
      ) {
        throw new ValidationError([
          {
            keyword: 'appBuilderLibVersion',
            message: `must be set to ${minSupportedVersion} or later when using mac.icon as an Apple .icon package`,
            instancePath: '/mac/icon',
          },
        ]);
      }

      return true;
    },
  });
}
